import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Zap,
  ShoppingBag,
  BarChart3,
  Globe,
  Mail,
  Shield,
  Layers,
} from 'lucide-react';
import MeshHero from '@/components/saas/MeshHero';
import Reveal from '@/components/saas/Reveal';
import MarketingCtaBand from '@/components/saas/MarketingCtaBand';
import { parseMarketingSections } from '@/lib/parse-marketing-html';
import { getCtaBanner, findSection, resolveIcon, type HomepageSection } from './helpers';
import ProseFallback from './ProseFallback';

const FEATURE_ICONS = [Sparkles, Zap, ShoppingBag, BarChart3, Mail, Globe, Shield, Layers];

interface Props {
  page: { title?: string; content?: string };
  sections?: HomepageSection[];
  siteName: string;
}

export default function TenantFeaturesPage({ page, sections, siteName }: Props) {
  const { t } = useTranslation('saas');
  const parsed = useMemo(() => parseMarketingSections(page.content), [page.content]);
  const cta = getCtaBanner(sections);

  const structured = parsed.sections.length > 0;
  const heading = page.title || t('tenantMarketing.viewFeatures');
  // Only promote the lead paragraph to the hero when we render cards below; in
  // the prose fallback the body owns the full content, so use a generic
  // subheading to avoid showing the lead paragraph twice.
  const subheading = structured
    ? parsed.intro[0] || t('tenantMarketing.featuresSubheadingFallback', { name: siteName })
    : t('tenantMarketing.featuresSubheadingFallback', { name: siteName });

  // Optional outcome strip from the tenant's stats_counter section, dropping
  // the generator's "—" placeholders.
  const statsSection = findSection(sections, 'stats_counter');
  const stats: Array<{ number: string; label: string; icon?: string }> = (statsSection?.content?.stats ?? [])
    .filter((s: any) => s && typeof s.number === 'string' && s.number.trim() && s.number.trim() !== '—');

  return (
    <>
      <MeshHero
        heading={heading}
        subheading={subheading}
        primaryCta={{ label: t('tenantMarketing.getStarted'), to: cta.ctaLink || '/signup' }}
        secondaryCta={{ label: t('tenantMarketing.viewPricing'), to: '/pricing' }}
      />

      {stats.length > 0 && (
        <section className="border-y border-sage-200 bg-sage-50">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
            <Reveal>
              <dl className="grid grid-cols-2 gap-8 sm:grid-cols-4">
                {stats.slice(0, 4).map((stat) => {
                  const Icon = resolveIcon(stat.icon);
                  return (
                    <div key={stat.label} className="min-w-0 text-center">
                      <Icon className="mx-auto h-6 w-6 text-sage-500" aria-hidden="true" />
                      <dd className="mt-2 font-display text-2xl font-bold text-sage-900 sm:text-3xl">
                        {stat.number}
                      </dd>
                      <dt className="mt-1 text-sm text-sage-600">{stat.label}</dt>
                    </div>
                  );
                })}
              </dl>
            </Reveal>
          </div>
        </section>
      )}

      {structured ? (
        <section className="bg-surface">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {parsed.sections.map((section, idx) => {
                const Icon = FEATURE_ICONS[idx % FEATURE_ICONS.length];
                return (
                  <Reveal key={`${section.title}-${idx}`} delayMs={(idx % 3) * 80} className="h-full">
                    <article className="group flex h-full min-w-0 flex-col rounded-2xl border border-sage-200 bg-surface p-6 ring-1 ring-sage-100 transition-all duration-200 hover:-translate-y-1 hover:border-sage-400 hover:shadow-lg focus-within:ring-2 focus-within:ring-sage-600 motion-reduce:hover:translate-y-0 lg:p-8">
                      <div className="inline-flex w-fit rounded-full bg-gradient-to-br from-sage-100 to-sage-50 p-2.5 ring-1 ring-sage-200 transition group-hover:ring-sage-400">
                        <Icon className="h-6 w-6 text-sage-700" aria-hidden="true" />
                      </div>
                      <h2 className="mt-4 font-display text-xl font-semibold text-sage-900">
                        {section.title}
                      </h2>
                      {section.body.map((p, i) => (
                        <p key={i} className="mt-2 leading-relaxed text-sage-600">
                          {p}
                        </p>
                      ))}
                      {section.bullets.length > 0 && (
                        <ul className="mt-4 space-y-2">
                          {section.bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-sage-700">
                              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-sage-400" aria-hidden="true" />
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                    </article>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      ) : (
        <ProseFallback html={page.content || ''} />
      )}

      <MarketingCtaBand
        heading={cta.heading || t('tenantMarketing.ctaHeadingFallback')}
        subheading={cta.subheading || t('tenantMarketing.ctaSubheadingFallback')}
        primaryLabel={cta.ctaText || t('tenantMarketing.getStarted')}
        primaryTo={cta.ctaLink || '/signup'}
        secondaryLabel={t('tenantMarketing.viewPricing')}
        secondaryTo="/pricing"
      />
    </>
  );
}
